import React from 'react';
import { css } from '@emotion/css';
import MUITab from '@mui/material/Tab';
import MUITabs from '@mui/material/Tabs';
import cn from 'classnames';

import CounterPill from '../Pill/CounterPill';
import { TabsProps, TabType } from './types';
import { indicator_animation, NEW_CONFIG } from './utils';

const tabFocusVisible = css`
  border-radius: 0.6rem;
  &.Mui-focusVisible {
    outline: 0.16rem solid transparent;
    box-shadow: inset 0 0 0 0.16rem var(--focus-secondary);
  }
`;

const disabledTab = css`
  cursor: not-allowed;
  opacity: 0.3;
`;

const DEFAULT_PRESSABLE_STATE: { readonly pressed: boolean } = { pressed: false };
const DefaultLink = React.forwardRef((props: any, ref) => {
  const { children, className, href, replace, target, to, ...rest } = props;
  const link = to || href;

  return (
    <a ref={ref} className={className} href={link} {...rest}>
      {typeof children === 'function' ? children(DEFAULT_PRESSABLE_STATE) : children}
    </a>
  );
});

function Tabs<T extends string = string>(props: TabsProps<T>) {
  const {
    appearance = 'primary',
    className,
    LinkComponent = DefaultLink,
    onChange,
    scrollable = true,
    tabs,
    value,
    variant = 'large',
  } = props;

  const tabsIndicatorRef = React.useRef<HTMLDivElement>(null);

  const _onChange = React.useCallback(
    (_: any, nextValue: T) => {
      onChange?.(nextValue);
    },
    [onChange],
  );

  const mergedConfig = React.useMemo(() => {
    return NEW_CONFIG[variant][appearance];
    // return _mergeWith(
    //   _merge({}, NEW_CONFIG[variant][appearance]),
    //   overrides,
    //   (objValue, srcValue) => {
    //     return typeof objValue === 'string' ? cn(objValue, srcValue) : undefined;
    //   },
    // );
  }, [variant, appearance]);

  React.useEffect(() => {
    const indicatorRef = tabsIndicatorRef.current;
    indicatorRef?.classList.add(indicator_animation);
    setTimeout(() => {
      indicatorRef?.classList.remove(indicator_animation);
    }, 200);
  }, [value]);

  const getLinkProps = React.useCallback(
    (to?: TabType['to']) => {
      if (!to) return {};
      return { component: LinkComponent, replace: true, to };
    },
    [LinkComponent],
  );

  return (
    <MUITabs
      TabIndicatorProps={{
        // @ts-ignore
        ref: tabsIndicatorRef,
      }}
      className={className}
      classes={mergedConfig.tabs_classes}
      scrollButtons={false}
      value={value}
      variant={scrollable ? 'scrollable' : 'standard'}
      onChange={_onChange}>
      {tabs.map((tab) => {
        return (
          <MUITab
            key={tab.value}
            className={cn(tabFocusVisible, tab.disabled && disabledTab)}
            classes={mergedConfig.tab_classes}
            data-tour-id={tab.tourId}
            disabled={tab.disabled}
            icon={
              !tab.count ? (
                <></>
              ) : (
                <CounterPill
                  className="ms-6"
                  label={tab.count}
                  size={variant === 'large' ? 'medium' : 'small'}
                />
              )
            }
            iconPosition={'end'}
            label={tab.label}
            value={tab.value}
            {...getLinkProps(tab.to)}
          />
        );
      })}
    </MUITabs>
  );
}

export default React.memo(Tabs) as typeof Tabs;
